import React, { useEffect, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from '../../../firebase/AuthProvider';
import { applyDocumentTheme, LAST_AUTHENTICATED_THEME_KEY, resolveAuthenticationTheme } from '../../../utilities/themePreference';
import { BrandLoader } from '../../ui/Logo/BrandLoader';
import { Moon, Sun } from 'lucide-react';
import Tooltip from '../../ui/Tooltip/Tooltip';
import { WaiveraLogo } from '../../ui/Logo/WaiveraLogo';
import { ROLES } from '../../../Data/SD';
import './FaqNavigation.css';
const FaqPortal = React.lazy(() => import('./FaqPortal'));

// Only reader URLs bypass the workspace gates and authenticated workspace providers.
export const isFaqReaderPath = (path: string) => /^\/faqs(?:\/[^/]+|\/article\/[^/]+)?\/?$/.test(path);
const PublicFaqRouteSwitch: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { currentUser, currentRoles, currentUserInfo, impersonatedUserId, isLoading, authInitializationError } = useAuth();
  const [theme, setTheme] = useState(resolveAuthenticationTheme);
  const publicPath = isFaqReaderPath(location.pathname);
  // Employee roles come from the effective identity bootstrap; a Firebase session alone
  // must not mount employee providers for participants or incomplete identity transitions.
  const employeeWorkspace = Boolean(currentUser && !isLoading && !authInitializationError &&
    currentUserInfo && !currentUserInfo.deactivated && currentRoles?.some(role => Object.values(ROLES).includes(role.name as ROLES)) &&
    currentUserInfo.roles?.some(role => Object.values(ROLES).includes(role.name as ROLES)) &&
    currentUserInfo.id === (impersonatedUserId || currentUser.uid));
  useEffect(() => { if (publicPath && !employeeWorkspace) applyDocumentTheme(theme); }, [publicPath, employeeWorkspace, theme]);
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try { localStorage.setItem(LAST_AUTHENTICATED_THEME_KEY, next); } catch { /* Session preference remains usable. */ }
  };
  if (!publicPath || employeeWorkspace) return <>{children}</>;
  return <div className={`faq-reader-shell wiki-shell${theme === 'dark' ? ' dark' : ''}`}>
    <header className="faq-public-header">
      <Link to="/faqs" className="faq-public-brand" aria-label="Waivera FAQ Center"><WaiveraLogo /><span>FAQ Center</span></Link>
      <nav aria-label="FAQ account navigation" className="faq-public-actions">
        <Tooltip content={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} theme={theme}>
          <button type="button" onClick={toggleTheme} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`} className="faq-theme-button">{theme === 'dark' ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}</button>
        </Tooltip>
        {!currentUser && <Link to="/login" state={{ from: location }} className="faq-nav-button">Sign in</Link>}
      </nav>
    </header>
    {isLoading ? <BrandLoader fullScreen /> : <React.Suspense fallback={<BrandLoader fullScreen />}><Routes>
      <Route path="/faqs" element={<FaqPortal publicLayout />} />
      <Route path="/faqs/article/:articleId" element={<FaqPortal publicLayout />} />
      <Route path="/faqs/:slug" element={<FaqPortal publicLayout />} />
    </Routes></React.Suspense>}
  </div>;
};
export default PublicFaqRouteSwitch;

